<template>
	<b-card-text>
		<div class="px-0 animated fadeIn" :class="`${$screen.width <= 576 ? 'container-mobile' : 'container-layout'}`">
			<div class="form form-overflow">
				<div v-for="(po, poIndex) in selectedPoWithItems" :key="po.id" class="mb-3" :ref="dynamicRef + 'A'">
					<div class="card-collapsible px-0">
						<div class="header-card-collapsible d-flex flex-row align-items-center cursor-pointer" v-b-toggle="po.id.toString()">
							<div class="title w-90">
								<span class="ml-3">{{ po.supplierName !== '' ? po.id + '-' + po.supplierName : po.id }}</span>
							</div>
							<div class="cursor-pointer d-flex justify-content-end w-10 when-closed">
								<component :is="getLucideIcon('ChevronDown')" color="#06263E" :size="22" />
							</div>
							<div class="cursor-pointer d-flex justify-content-end w-10 when-open">
								<component color="#06263E" :is="getLucideIcon('ChevronUp')" :size="22" />
							</div>
						</div>
					</div>
					<b-collapse :id="po.id.toString()">
						<div class="content-card-collapsible">
							<div v-if="$screen.width <= 992" class="container-mobile">
								<PoTransactionDataMobile
									:allSelected="po.isSelectedAll"
									:selectedDataRows="selectedRows"
									:poItemLists="po.poItems"
									:purchaseOrder="po"
									:poIndex="poIndex"
									@setSelectionForAll="setSelectionForAll"
									@updateSelectedRows="(poItem, poIndex, purchaseOrder) => updateSelectedRows(poItem, poIndex, purchaseOrder)"
									@handleChangeAction="handleChangeAction"
								/>
							</div>
							<b-table
								v-else
								id="table-clickable"
								:hover="hover"
								responsive="sm"
								:filter="filter"
								:items="po.poItems"
								:fields="poItemFields"
								:current-page="currentPage"
								:per-page="0"
								bordered
								outlined
								small
								sticky-header="700px"
								:head-row-variant="hv"
								:empty-text="FormMSG(10, 'No Po items found')"
								show-empty
							>
								<template #head(select)="">
									<b-form-checkbox size="lg" v-model="po.isSelectedAll" @change="(e) => setSelectionForAll(po, poIndex, e)" />
								</template>
								<template #cell(select)="data">
									<span>
										<b-form-checkbox
											size="lg"
											class="pj-cb pb-1"
											v-model="data.item.selectState"
											@change="updateSelectedRows(po, poIndex, data.item)"
										/>
									</span>
								</template>
								<template #cell(action)="data">
									<div>
										<!-- <pre>{{ data.item }}</pre> -->
										<v-select
											:ref="generateId(data.item.id)"
											class="w-100 absolute grow-flex-auto"
											v-model="data.item.action"
											:placeholder="FormMSG(11, 'choose...')"
											label="text"
											:options="getActionsMenu"
											:appendToBody="true"
											:reduce="(option) => option.value"
											@input="handleChangeAction(data.item)"
											:class="{ 'is-invalid': data.item.selectState && !data.item.action }"
										/>
									</div>
								</template>
								<template #cell(categoryCostCenter)="{ item }">
									<span> {{ item.categoryCostCenter }}-{{ item.categoryName }} </span>
								</template>
							</b-table>
						</div>
					</b-collapse>
				</div>
			</div>
		</div>
	</b-card-text>
</template>

<script>
// utils
import { rendCurrency } from '~helpers';
import _ from 'lodash';
import { isNil, generateSecureId } from '@/shared/utils';
// mixin
import globalMixin from '@/mixins/global.mixin';
import languageMessages from '@/mixins/languageMessages';
import invoicesTools from '@/mixins/invoicesTools.mixin';
// props
import mapProps from '@/shared/vuePropsMapper';
// cruds
import { getSpecificPurchaseOrdersForInvoice } from '@/cruds/purchase-order.crud';
// constants
import { INIT_DATA_CARBON, EXPENSE_TYPE } from '@/shared/constants';
import PoTransactionDataMobile from './PoTransactionDataMobile.vue';

export default {
	name: 'PoTransactionDataView',
	mixins: [languageMessages, globalMixin, invoicesTools],
	components: {
		PoTransactionDataMobile
	},
	props: {
		...mapProps(['hover', 'striped', 'bordered'], {
			type: Boolean,
			default: true
		}),
		...mapProps(['small', 'fixed'], {
			type: Boolean,
			default: false
		}),
		invoiceDate: {
			type: String | Date,
			required: true
		},
		selectedPo: {
			type: Array,
			required: true,
			default: () => []
		},
		selectedItems: {
			type: Array,
			required: true,
			default: () => []
		}
	},
	data() {
		return {
			filter: '',
			isBusy: false,
			currentPage: 1,
			hv: 'dark',
			invoiceType: 1,
			selectedPoWithItems: [],
			selectedRows: [],
			dynamicRef: 0,
			transactionTypeTpl: 'transaction-type-'
		};
	},
	async mounted() {
		await this.initData();
	},
	computed: {
		poItemFields() {
			return [
				{
					key: 'select',
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'action',
					label: this.FormMSG(2, 'Action'),
					formatter: (v) => `${v}`,
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff', 'min-width': '200px' }
				},
				{
					key: 'categoryCostCenter',
					label: this.FormMSG(3, 'Cost code'),
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'description',
					label: this.FormMSG(5, 'Description'),
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'priceTotal',
					label: this.FormMSG(6, 'Net'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right',
					thStyle: { color: '#fff', 'min-width': '70px' }
				},
				{
					key: 'priceVatTotal',
					label: this.FormMSG(7, 'Tax'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right',
					thStyle: { color: '#fff', 'min-width': '70px' }
				},
				{
					key: 'total',
					label: this.FormMSG(9, 'Initial Amount Total'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right',
					thStyle: { color: '#fff' }
				},
				{
					key: 'remainingAmount',
					label: this.FormMSG(13, 'Remaining Amount'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right',
					thStyle: { color: '#fff' }
				}
			];
		}
	},
	methods: {
		async initData() {
			try {
				// reinitialize selections
				if (this.selectedItems.length > 0) {
					this.selectedRows = this.selectedItems;
				} else {
					this.selectedRows = [];
				}

				// initialize data
				const poIds = this.selectedPo.map((x) => parseInt(x.id, 10));
				const result = await getSpecificPurchaseOrdersForInvoice(poIds);
				this.selectedPoWithItems = result.map((po, index) => {
					const cleanItems = this.provideCleanItems(po.poItems);
					return {
						...po,
						...cleanItems,
						supplierId: this.selectedPo[index].supplierId,
						supplierName: this.selectedPo[index].supplierName
					};
				});
			} catch (error) {
				console.log(error);
			}
		},
		initActions(item) {
			if (item.action) {
				return item.action;
			} else {
				const itemInSelection = this.selectedRows.find((row) => row.poItemId === +item.id);
				if (itemInSelection && !isNil(itemInSelection.action)) {
					return itemInSelection.action;
				} else {
					return undefined;
				}
			}
		},
		isAllItemSelected(poItems) {
			return poItems.every(({ selectState }) => selectState);
		},
		provideCleanItems(records) {
			const reducedSelectedRows = this.selectedRows.map((x) => x.poItemId);
			const newRecords = records.map((item) => {
				return {
					...item,
					action: this.initActions(item),
					selectState: reducedSelectedRows.includes(+item.id)
				};
			});
			return {
				poItems: newRecords,
				isSelectedAll: this.isAllItemSelected(newRecords)
			};
		},
		translateItemToInvoice(item) {
			const invoiceItem = {
				id: null,
				date: this.invoiceDate,
				poId: item.poId,
				poItemId: +item.id,
				expenseItemId: null,
				type: EXPENSE_TYPE.INVOICE,
				description: item.description,
				category: item.category,
				costCenter: item.categoryCostCenter,
				categoryName: item.categoryName,
				amount: parseFloat(item.priceTotal), // price * quantity
				amountVat: parseFloat(item.priceVatTotal), // totalPriceVat
				amountTotal: parseFloat(item.total) === parseFloat(item.remainingAmount) ? parseFloat(item.total) : parseFloat(item.remainingAmount),
				quantity: item.quantity,
				poItemRemaining: 0,
				action: item.action,
				projectFlagItems: item.projectFlagItems ? item.projectFlagItems : { customFlags: [], taxeFlags: [] },
				carbon: item.carbon ? item.carbon : INIT_DATA_CARBON
			};
			return invoiceItem;
		},
		updateSelectedRows(po, poIndex, selectedItem) {
			const foundIndex = this.selectedRows.findIndex((x) => x.poItemId === +selectedItem.id);
			if (foundIndex !== -1) {
				this.selectedRows.splice(foundIndex, 1);
				// reset select input
				const foundPoItemIndex = this.selectedPoWithItems[poIndex].poItems.findIndex((item) => +item.id === +selectedItem.id);
				if (foundPoItemIndex !== -1) {
					this.selectedPoWithItems[poIndex].poItems[foundPoItemIndex].action = null;
				}
			} else {
				this.selectedRows.push(this.translateItemToInvoice(selectedItem));
			}

			const updatedPo = {
				...po,
				isSelectedAll: this.isAllItemSelected(po.poItems)
			};

			this.selectedPoWithItems[poIndex] = updatedPo;

			this.emitSelection();
			this.dynamicRef++;
		},
		setItemsSelectState(items, state) {
			return items.map((x) => {
				return { ...x, selectState: state };
			});
		},
		setSelectionForAll(po, poIndex, event) {
			const updatedPo = {
				...po,
				isSelectedAll: event,
				poItems: this.setItemsSelectState(po.poItems, event)
			};
			if (event) {
				for (const item of updatedPo.poItems) {
					this.selectedRows.push(this.translateItemToInvoice(item));
				}
			} else {
				for (const item of updatedPo.poItems) {
					const itemIndex = this.selectedRows.findIndex((x) => x.poItemId === +item.id);
					if (itemIndex !== -1) this.selectedRows.splice(itemIndex, 1);
				}
			}

			this.selectedPoWithItems[poIndex] = updatedPo;

			this.emitSelection();
			this.dynamicRef++;
		},
		handleChangeAction(item) {
			const isItemSelectedIndex = this.selectedRows.findIndex((e) => e.poItemId === +item.id);
			if (isItemSelectedIndex !== -1) {
				this.selectedRows[isItemSelectedIndex].action = item.action;
				this.emitSelection();
			}
			this.dynamicRef++;
		},
		emitSelection() {
			this.$emit('selection:change', this.selectedRows);
		},
		generateId(id) {
			return generateSecureId(`${this.transactionTypeTpl}${id}`);
		}
	},
	watch: {
		selectedRows: {
			handler(newVal) {
				let enableContinue = false;
				if (newVal.length > 0) {
					const missingAction = newVal.find((element) => isNil(element.action));
					if (!missingAction) enableContinue = true;
				}
				this.$emit('enable:continue', enableContinue);
			},
			deep: true,
			immediate: true
		}
	}
};
</script>

<style scoped>
.collapsed > .when-open,
.not-collapsed > .when-closed {
	display: none !important;
}

:deep(.vertical-centered) {
	vertical-align: middle !important;
}
.form-overflow {
	height: 500px;
	overflow: auto;
}
</style>
